import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import defaultLogo from "@/assets/logo.webp";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/hooks/useSiteContent";
import { resolvePostLoginPath, setAdminBootstrapSession } from "@/lib/accessControl";
import { getPublicOrigin } from "@/lib/publicOrigin";
import { syncStaffPasswordForLogin } from "@/lib/staffPasswordSync";

const Auth = () => {
  const { content: siteSettings } = useSiteContent();
  const logoImg = siteSettings.logo_url?.trim() || defaultLogo;
  const [mode, setMode] = useState<"login" | "signup" | "forgot">("login");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const clear = () => { setError(""); setMessage(""); };

  const getAuthEmail = () => identifier.trim().toLowerCase();

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    clear();
    const { error } = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (error) setError(error.message);
    setGoogleLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    clear();

    try {
      const authEmail = getAuthEmail();

      if (!authEmail) {
        setError("Please enter a valid email");
        return;
      }

      if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(authEmail, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) setError(error.message);
        else setMessage(t("reset_link_sent"));
        return;
      }

      if (mode === "login") {
        let { data, error } = await supabase.auth.signInWithPassword({ email: authEmail, password });
        if (error) {
          const synced = error.message.toLowerCase().includes("invalid login credentials")
            ? await syncStaffPasswordForLogin(authEmail, password)
            : false;

          if (synced) {
            const retry = await supabase.auth.signInWithPassword({ email: authEmail, password });
            data = retry.data;
            error = retry.error;
          }

          if (error) {
            setError(error.message);
            return;
          }
        }

        if (data.session) {
          await supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
        }

        let nextPath = "/";
        try {
          nextPath = await resolvePostLoginPath(data.user.id, data.session?.access_token);
        } catch {
          // Role resolution failed — default to home for customers
          nextPath = "/";
        }

        if (nextPath.startsWith("/admin")) {
          if (!data.session?.access_token) {
            setError("There was a problem loading the session. Please log in again.");
            await supabase.auth.signOut();
            return;
          }

          setAdminBootstrapSession({
            userId: data.user.id,
            accessToken: data.session.access_token,
            refreshToken: data.session.refresh_token,
          });
          navigate(nextPath, { replace: true });
          return;
        }

        navigate(nextPath, { replace: true });
        return;
      }

      const { data: signUpData, error } = await supabase.auth.signUp({
        email: authEmail,
        password,
        options: { emailRedirectTo: window.location.origin },
      });

      if (error) {
        setError(error.message);
        return;
      }

      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 flex items-center gap-1.5 text-sm font-body text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft size={18} />
        <span className="hidden sm:inline">{t("back")}</span>
      </button>

      <div className="w-full max-w-md">
        <a href="/" className="flex justify-center mb-8">
          <img src={logoImg} alt="Fatema Farista" className="h-16 w-auto" />
        </a>

        <div className="bg-card border border-border p-8">
          <h2 className="font-heading text-2xl text-foreground text-center mb-2">
            {mode === "login" ? t("welcome_back") : mode === "signup" ? t("create_account") : t("reset_password")}
          </h2>
          <p className="text-xs text-muted-foreground text-center mb-6 font-body tracking-wider">
            {mode === "login" ? t("login_subtitle") : mode === "signup" ? t("signup_subtitle") : t("enter_email")}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-body tracking-wider uppercase text-muted-foreground mb-1.5 block">
                {t("email")}
              </label>
              <input
                type="email"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className="w-full bg-background border border-border px-4 py-3 text-sm font-body text-foreground focus:border-primary focus:outline-none transition-colors"
                placeholder={t("your_email")}
              />
            </div>

            {mode !== "forgot" && (
              <div>
                <label className="text-xs font-body tracking-wider uppercase text-muted-foreground mb-1.5 block">{t("password")}</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="w-full bg-background border border-border px-4 py-3 text-sm font-body text-foreground focus:border-primary focus:outline-none transition-colors pr-10" placeholder="••••••••" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {mode === "login" && (
                  <button type="button" onClick={() => { setMode("forgot"); clear(); }} className="text-[11px] text-muted-foreground hover:text-foreground font-body mt-1.5 transition-colors">
                    {t("forgot_password")}
                  </button>
                )}
              </div>
            )}

            {error && <p className="text-xs text-destructive font-body">{error}</p>}
            {message && <p className="text-xs text-primary font-body">{message}</p>}

            <button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground py-3 text-sm font-body tracking-wider uppercase hover:bg-gold-light transition-colors duration-300 disabled:opacity-50">
              {loading ? t("loading") : mode === "login" ? t("login") : mode === "signup" ? t("sign_up") : t("send_reset_link")}
            </button>
          </form>

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-[1px] bg-border" />
                <span className="text-xs font-body text-muted-foreground tracking-wider">{t("or")}</span>
                <div className="flex-1 h-[1px] bg-border" />
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-3 border border-border py-3 text-sm font-body tracking-wider hover:bg-muted/50 transition-colors disabled:opacity-50"
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {googleLoading ? t("connecting") : t("continue_google")}
              </button>
            </>
          )}

          <div className="gold-divider mt-6 mb-4" />

          <p className="text-center text-xs font-body text-muted-foreground">
            {mode === "login" ? (
              <>{t("no_account")}{" "}<button onClick={() => { setMode("signup"); clear(); }} className="text-foreground hover:text-gold-light transition-colors underline">{t("sign_up")}</button></>
            ) : (
              <>{t("have_account")}{" "}<button onClick={() => { setMode("login"); clear(); }} className="text-foreground hover:text-gold-light transition-colors underline">{t("login")}</button></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;